import { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { AccountBannerStore } from 'account/account-banner';
import { Button as ButtonX } from 'account/recurring-payment/components/common';
import tracking from 'account/recurring-payment/lib/tracking';
import { Button } from 'components/buttons/button';
import { ButtonSize, ButtonType } from 'components/buttons/button-types';
import { AppContext } from 'core/context/app.context';
import { FeatureFlag } from 'core/enums/feature-flag';
import { AmountToPayCardProps } from './amount-to-pay-card-props';
import { AmountInput } from '../components/amount-input';
import { AmountInputMessage } from '../components/amount-input-message';

type AmountToPayCompactCardProps = AmountToPayCardProps & {
  styles: { [key: string]: string };
}

export const AmountToPayCompactCard = observer<AmountToPayCompactCardProps>((props) => {
  const { styles, onPayNow, isPaymentBlocked, amountToPayStore, analyticsEntryPoint, onSetupRecurringPayment } = props;
  const bannerStore = useLocalObservable(() => new AccountBannerStore());
  const { currencyStore, featureStore, recurringPaymentStore } = useContext(AppContext);
  const { t } = useTranslation();

  const tcsApply = featureStore.hasViewPermission(FeatureFlag.AccountTsAndCsApply);

  const hasRecurringPaymentFeature = featureStore.hasViewPermission(FeatureFlag.RecurringPaymentRoute);
  const { hasRecurringPayment, isInitialized, hasConnectedService } = recurringPaymentStore;
  const showSetupRecurringPaymentButton = hasRecurringPaymentFeature && isInitialized && !hasRecurringPayment;
  const isRecurringPaymentButtonEnabled = hasConnectedService;

  useEffect(() => {
    if (tcsApply) {
      bannerStore.initialize(featureStore.hasViewPermission(FeatureFlag.AccountTsAndCsBannerUseMahLink));
    }
  }, []);

  return (
    <div
      className={`text-small-bold text-light-70 justify-content-center text-center ${styles.cardContainer} ${styles.compact}`}
    >
      <h5 className="text-regular-bold">{t('account.amountToPay.paymentReminder')}</h5>

      <AmountInput
        id="compactAmountToPayInput"
        amountToPayStore={amountToPayStore}
        allowEdit={featureStore.hasEditPermission(FeatureFlag.AllowCompactAmountToPayEdit)}
        styleConfig={{
          buttonType: ButtonType.Light,
          inputClass: styles.textInputCompact,
          alignment: 'center',
        }}
      />

      <div className="text-small-bold text-light-70 text-hug">
        <Trans
          i18nKey="account.amountToPay.dueByPayment"
          defaults="due by {{ dueDate, datetime }}"
          values={{
            dueDate: amountToPayStore?.dueDate,
            amountDue: `${amountToPayStore?.formattedValue}`,
            formatParams: {
              dueDate: { day: 'numeric', month: 'long' },
            },
          }}
          components={{ 1: <span /> }}
        ></Trans>
      </div>

      <AmountInputMessage amountToPayStore={amountToPayStore} currencyStore={currencyStore} />

      <div className={`${styles.buttonContainer} d-flex justify-content-center cust-mt-2 flex-column`}>
        {isPaymentBlocked ? (
          <div className="text-small">{t('common.paymentBlocked')}</div>
        ) : (
          <Button
            meta={{
              type: ButtonType.Primary,
              size: ButtonSize.Small,
              title: t('buttons.payNow'),
              name: 'payNowButton',
              areaName: 'Self Service Accounts - Sidebar',
              actionName: 'payNow',
              componentName: 'amountToPayCard',
              disabled: amountToPayStore.disablePayButton,
              entryPoint: analyticsEntryPoint,
            }}
            classes={['align-self-center', 'full-width', 'p-0']}
            onClick={onPayNow}
            analyticsComponentName="amountToPayButton"
          ></Button>
        )}
        {showSetupRecurringPaymentButton && <ButtonX
          meta={{
            ...tracking.amountToPay.setupReminder,
            type: ButtonType.Light,
            size: ButtonSize.Small,
            title: t('account.amountToPay.setupRecurringPayment'),
            disabled: !isRecurringPaymentButtonEnabled
          }}
          classes={['align-self-center', 'full-width', 'p-0', 'mt-2']}
          onClick={onSetupRecurringPayment}
        />}
      </div>
      {tcsApply && (
        <div className="cust-mt-2">
          <a
            className="text-link-underline text-small-bold"
            href={bannerStore.bannerLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('signedOut.tcApply')}
          </a>
        </div>
      )}
    </div>
  );
});